import { ComponentType, ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { ElementRef, Injectable, Injector } from '@angular/core';
import { ConnectedDialogPositions } from './connected-dialog-positions';
import { CONNECTED_DIALOG_DATA } from './connected-dialog.tokens';
import { Subject } from 'rxjs';

@Injectable()
export class ConnectedDialogService {

  overlayRef: OverlayRef;

  private onClosed = new Subject<any>();
  onClosed$ = this.onClosed.asObservable();

  constructor(private injector: Injector,
              private overlay: Overlay) { }

  open<T>(componentType: ComponentType<T>, elementRef: ElementRef,
          position: ConnectedDialogPositions, data: {}) {
    const overlayRef = this.overlay.create(this.getOverlayConfig(elementRef, position));

    const componentPortal = new ComponentPortal(
      componentType,
      null,
      this.createInjector(data)
    );

    overlayRef.attach(componentPortal);
    this.overlayRef = overlayRef;
  }

  close(data?: any) {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.onClosed.next(data);
  }

  createInjector(data: any): PortalInjector {
    const injectorTokens = new WeakMap();
    injectorTokens.set(CONNECTED_DIALOG_DATA, data);
    injectorTokens.set(ConnectedDialogService, this);
    return new PortalInjector(this.injector, injectorTokens);
  }

  getOverlayConfig(parentElement: ElementRef, position: ConnectedDialogPositions): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'mstar-connected-dialog-backdrop',
      positionStrategy: this.overlay.position()
                                    .flexibleConnectedTo(parentElement)
                                    .withPositions(this.getPositions(position))
    });
  }

  getPositions(position: ConnectedDialogPositions): ConnectedPosition[] {
    const bottomPosition: ConnectedPosition = {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      panelClass: 'mstar-connected-dialog-container'
    };

    const topPosition: ConnectedPosition = {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      panelClass: 'mstar-connected-dialog-container'
    };

    const startPosition: ConnectedPosition = {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      panelClass: 'mstar-connected-dialog-container'
    };

    const endPosition: ConnectedPosition = {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      panelClass: 'mstar-connected-dialog-container'
    };

    let positions = [];

    switch (position) {
      case 'top':
        positions = [ topPosition, bottomPosition, startPosition, endPosition ];
        break;
      case 'bottom':
        positions = [ topPosition, bottomPosition, startPosition, endPosition ];
        break;
      case 'start':
        positions = [ startPosition, endPosition, topPosition, bottomPosition ];
        break;
      case 'end':
        positions = [ endPosition, startPosition, topPosition, bottomPosition ];
        break;
    }

    return positions;
  }
}
